// src/components/guides/RelationshipCompassQuickGuideHub.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb,
  Zap,
  X,
  ChevronRight,
  ChevronLeft,
  Star,
  MessageSquare,
  Target,
  Brain,
  Users,
  BookOpen,
  Eye,
  Sparkles,
  Award,
  TrendingUp,
  Heart,
  MessageCircle,
  Shield,
  Smile,
  AlertCircle,
  CheckCircle,
  ThumbsUp,
  Flame,
  Lock,
  TreePine,
  Compass
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import {
  StepsLayout,
  TypesLayout,
  TechniquesLayout,
  HacksLayout,
  CategoriesLayout,
  BlocksLayout
} from './QuickGuideLayouts';

const RelationshipCompassQuickGuideHub = ({ isOpen, onClose }) => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [favouriteGuides, setFavouriteGuides] = useState([]);
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = (text, itemId) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const toggleFavourite = (guideId) => {
    if (favouriteGuides.includes(guideId)) {
      setFavouriteGuides(favouriteGuides.filter(id => id !== guideId));
    } else {
      setFavouriteGuides([...favouriteGuides, guideId]);
    }
  };

  const quickGuides = [
    // ==========================================
    // INTRODUCTION
    // ==========================================
    {
      id: 'compass-intro',
      category: 'Getting Started',
      title: 'How to Use This Compass',
      icon: Compass,
      color: 'purple',
      teaser: 'Your quick reference for relationship skills',
      content: {
        intro: 'This is the Relationship Compass: a quick reference companion for the 8 core relationship modules. Think of it as your relationship health field guide.',
        blocks: [
          {
            name: '🧭 What This Compass Is',
            tagline: 'Quick reference, not replacement',
            signs: [
              'Distilled principles from the 8 learning modules',
              'Practical techniques you can use immediately',
              'Quick refreshers when you need them',
              'Reference guides for real-life moments'
            ],
            inAction: 'About to have difficult conversation? Check the Communication guide. Feeling disconnected? Review the Intimacy principles.',
            quickFix: 'Use these guides as reminders alongside the full modules',
            deeperTruth: 'These guides complement the modules. They don\'t replace them'
          },
          {
            name: '🎯 How to Get the Most From It',
            tagline: 'Strategic, not sequential',
            signs: [
              'You don\'t need to read every guide in order',
              'Jump to what you need when you need it',
              'Revisit guides as you practise the skills',
              'Star your favourites for quick access'
            ],
            inAction: 'Navigating conflict? Check the Conflict guide. Planning your future? Review the Growth & Vision guide.',
            quickFix: 'Use categories to find what you need in the moment',
            deeperTruth: 'The best time to use a guide is right before you need the skill'
          },
          {
            name: '⚡ The Learning Loop',
            tagline: 'Modules first, compass second',
            signs: [
              'Complete the modules for deep learning',
              'Use the compass for quick refreshers',
              'Practise the techniques in your relationship',
              'Return to guides when you need reminders'
            ],
            inAction: 'Module → Practice → Compass → Real Interaction → Reflect → Repeat',
            quickFix: 'Don\'t skip the modules. That\'s where the real change happens',
            deeperTruth: 'Reading about relationships ≠ having a thriving relationship. Consistent practice does.'
          },
          {
            name: '🔬 Research-Backed Principles',
            tagline: 'Science, not tricks',
            signs: [
              'Draws on research and clinical work (Gottman, Johnson, Bowen, Schnarch)',
              'Grounded in relationship science',
              'Evidence-based techniques, not pseudoscience',
              'Principles you can adapt to your unique relationship'
            ],
            inAction: 'Every technique comes with a "why it works", and we say where an idea is our own',
            quickFix: 'These aren\'t manipulation tactics. They\'re how thriving relationships actually work',
            deeperTruth: 'Great relationships are built through learnable skills, not luck'
          }
        ],
        bottomLine: 'This compass is your companion, not your teacher. The modules teach. This reminds. You practise with your partner. That\'s the path to lasting love.',
        challenge: 'Start with the modules. Return here when you need quick guidance. Your relationship will thank you.'
      }
    },

    // ==========================================
    // MODULE 1: APPRECIATION & CONNECTION
    // ==========================================
    {
      id: 'appreciation-essentials',
      category: 'Foundation Skills',
      title: 'Appreciation Essentials',
      icon: Heart,
      color: 'rose',
      teaser: 'The 5:1 ratio and daily connection',
      content: {
        intro: 'Appreciation is the foundation of positive sentiment override. In Gottman\'s research, couples who stayed together showed about 5 positive interactions for every 1 negative when they disagreed. This isn\'t optional. It\'s essential maintenance.',
        techniques: [
          {
            name: 'The 5:1 Ratio Rule',
            howTo: [
              'Track your daily positive vs negative interactions',
              'Aim for 5 positive moments for every 1 negative',
              'Positive = appreciation, affection, humour, interest, turning toward',
              'Negative = criticism, dismissiveness, irritation'
            ],
            example: 'Morning kiss (1), "thanks for coffee" (2), laugh together (3), interested in their day (4), physical affection (5) = bank account for one criticism',
            why: 'Positive sentiment override protects relationship during conflicts and stressful times'
          },
          {
            name: 'Specific Appreciation',
            howTo: [
              'Name the specific action or quality',
              'Explain the impact it had on you',
              'Express genuine gratitude',
              'Make it personal and detailed'
            ],
            example: 'NOT: "You\'re great." YES: "When you remembered I had that meeting and texted to ask how it went, I felt really supported and thought about."',
            why: 'Specific appreciation shows you\'re paying attention and validates actual effort'
          },
          {
            name: 'Turning Toward Bids',
            howTo: [
              'Notice when partner makes "bid" for connection (comment, question, touch, joke)',
              'Stop what you\'re doing and engage',
              'Aim to turn towards most bids (newlyweds who stayed married averaged about 86% in Gottman\'s study)',
              'Newlyweds who later divorced had turned towards only about 33%'
            ],
            example: 'Partner: "Look at this funny thing" → You: PUT PHONE DOWN, actually look, respond with interest',
            why: 'These micro-moments accumulate into either connection or distance over time'
          },
          {
            name: 'Daily Appreciation Ritual',
            howTo: [
              'Set a specific time (morning, evening, bedtime)',
              'Each person shares one specific appreciation',
              'Keep it brief (30 seconds each)',
              'Make it daily, not occasional'
            ],
            example: 'Every evening before bed: "Today I appreciated when you..." Creates predictable positive interaction',
            why: 'Ritual ensures appreciation happens consistently, not just when remembered'
          }
        ],
        remember: 'Appreciation isn\'t about being fake-positive. It\'s about noticing and acknowledging real positive things that already exist.',
        practice: 'This week: Count your 5:1 ratio daily. Many people are surprised by how often small negatives outnumber the positives.',
        proTip: 'The best time to build emotional bank account is during good times, before you need to make withdrawals during conflict.'
      }
    },

    {
      id: 'emotional-awareness',
      category: 'Foundation Skills',
      title: 'Emotional Awareness',
      icon: Smile,
      color: 'amber',
      teaser: 'Identifying, expressing, and validating feelings',
      content: {
        intro: 'Most relationship problems aren\'t about the topic; they\'re about unidentified or invalidated emotions. Learning to name and validate emotions creates the safety needed for genuine intimacy.',
        types: [
          {
            name: '🎯 Use the Feeling Wheel',
            purpose: 'Move beyond "fine," "good," "bad," "stressed"',
            why: 'Precision in emotion leads to precision in response',
            examples: [
              'Core: Happy → Secondary: Optimistic → Tertiary: Hopeful',
              'Core: Sad → Secondary: Lonely → Tertiary: Isolated',
              'Core: Angry → Secondary: Frustrated → Tertiary: Helpless',
              'Core: Scared → Secondary: Anxious → Tertiary: Overwhelmed'
            ],
            magic: 'Naming emotions precisely reduces their intensity (affect labelling)',
            avoid: 'Staying at surface level ("I\'m fine") when emotions are actually complex'
          },
          {
            name: '💬 Express Without Blaming',
            purpose: 'Own your emotions instead of weaponising them',
            why: 'Taking responsibility for feelings reduces defensiveness',
            examples: [
              'NOT: "You made me angry" → YES: "I felt angry when..."',
              'NOT: "You always make me feel ignored" → YES: "I feel ignored when my bids aren\'t responded to"',
              'NOT: "You hurt me" → YES: "I felt hurt when..."',
              'Taking ownership ≠ saying they did nothing wrong'
            ],
            magic: '"I feel..." statements are hard to argue with: they\'re your experience',
            avoid: 'Using "I feel" to sneak in criticism: "I feel like you\'re an idiot"'
          },
          {
            name: '✅ Validate Without Agreeing',
            purpose: 'Acknowledge their emotional experience as real',
            why: 'Validation creates safety; invalidation shuts down vulnerability',
            examples: [
              '"That makes sense that you\'d feel that way"',
              '"I can see why that would be [emotion] for you"',
              '"Your feelings are valid, even if I see it differently"',
              '"Tell me more about what that feels like"'
            ],
            magic: 'Validation ≠ agreement. You can validate their experience whilst having different perspective',
            avoid: 'Minimising ("You\'re overreacting"), fixing ("Just do X"), or competing ("Well I feel...")'
          },
          {
            name: '🛑 Recognise Flooding',
            purpose: 'Know when you\'re too activated to communicate well',
            why: 'Flooded conversations damage relationships. They never go well',
            examples: [
              'Physical: Heart racing, tight chest, hot face',
              'Cognitive: Can\'t think clearly, overwhelmed, reactive',
              'Behavioural: Saying things you regret, can\'t listen',
              'When flooded: Take 20-min break, self-soothe, return when calm'
            ],
            magic: 'Taking a proper break when flooded stops a fight doing damage you\'ll both regret',
            avoid: 'Pushing through when flooded (you\'ll just cause damage)'
          }
        ],
        remember: 'Emotions aren\'t problems to fix. They\'re information to understand. Your job is to create safety for emotions, not eliminate them.',
        challenge: 'This week: Use the Feeling Wheel daily to identify your emotions with precision. Notice how naming them changes your experience.',
        proTip: 'When partner shares emotions, your only job is to RECEIVE, not fix, defend, or counter with your emotions.'
      }
    },

    {
      id: 'communication-fundamentals',
      category: 'Foundation Skills',
      title: 'Communication Fundamentals',
      icon: MessageCircle,
      color: 'blue',
      teaser: 'Active listening and the Four Horsemen antidotes',
      content: {
        intro: 'Communication is the vehicle for everything else. In Gottman\'s studies, how couples talked during a short conflict discussion was strongly linked with whether they later divorced. Master these fundamentals.',
        blocks: [
          {
            name: '👂 The LAER Listening Method (adapted from sales training)',
            tagline: 'Listen → Acknowledge → Explore → Respond',
            signs: [
              'LISTEN: Full attention, no interrupting, no planning your response',
              'ACKNOWLEDGE: "What I\'m hearing is..." (reflect back)',
              'EXPLORE: "Tell me more about..." (curious questions)',
              'RESPOND: Only after fully understanding their perspective'
            ],
            inAction: 'Partner shares problem → You LAER (don\'t jump to solutions) → They feel heard → Actual communication happens',
            quickFix: 'Most "communication problems" are actually listening problems',
            deeperTruth: 'Listening to understand ≠ listening to respond. They feel completely different to speaker.'
          },
          {
            name: '🐴 Recognise the Four Horsemen',
            tagline: 'Criticism, Contempt, Defensiveness, Stonewalling',
            signs: [
              'CRITICISM: Attacking character ("You always/never...") vs complaint about specific behaviour',
              'CONTEMPT: Mockery, sarcasm, eye-rolling, name-calling (most toxic)',
              'DEFENSIVENESS: "Yes, but..." or counter-attack instead of taking responsibility',
              'STONEWALLING: Shutting down, giving silent treatment, refusing to engage'
            ],
            inAction: 'These predict divorce because they destroy connection. Once they become patterns, relationship is in serious danger.',
            quickFix: 'Spotting these in yourself is more important than spotting them in partner',
            deeperTruth: 'Gottman identifies contempt as the single greatest predictor of divorce'
          },
          {
            name: '💊 Use the Antidotes',
            tagline: 'Replace Horsemen with healthy alternatives',
            signs: [
              'Criticism → GENTLE STARTUP: "I feel [emotion] about [situation]. I need [request]"',
              'Contempt → BUILD CULTURE OF APPRECIATION: Daily fondness and admiration',
              'Defensiveness → TAKE RESPONSIBILITY: "You\'re right, I did [thing]. I\'m sorry."',
              'Stonewalling → SELF-SOOTHE: "I need a break. I\'ll be back in 20 minutes."'
            ],
            inAction: 'Notice Horseman appearing → Stop → Use antidote → Conversation quality immediately improves',
            quickFix: 'Gottman reports the first three minutes predict how a conflict conversation ends 96% of the time. Start conversations well',
            deeperTruth: 'How you START difficult conversations predicts how they\'ll END'
          },
          {
            name: '🎯 "I" Statements',
            tagline: 'Express needs without attacking',
            signs: [
              'Format: "I feel [emotion] when [situation] because [impact]. I need [request]."',
              'NOT: "You never listen!" → YES: "I feel unheard when interrupted. I need space to finish my thought."',
              'Takes ownership of your experience',
              'Makes specific, doable request'
            ],
            inAction: 'Partner can\'t argue with your feelings: they\'re your experience. Makes defensiveness less likely.',
            quickFix: 'If your sentence starts with "You...", pause and reframe to "I..."',
            deeperTruth: 'Taking responsibility for your feelings paradoxically makes partner more likely to take responsibility for their behaviour'
          }
        ],
        bottomLine: 'Great communication isn\'t about never having conflict. It\'s about having conflict without destroying connection.',
        practice: 'This week: Catch yourself using a Horseman. Stop. Use the antidote. Notice how it changes the interaction.'
      }
    },

    // ==========================================
    // MODULE 4: NAVIGATING CONFLICT
    // ==========================================
    {
      id: 'conflict-navigation',
      category: 'Challenge Navigation',
      title: 'Conflict Navigation',
      icon: AlertCircle,
      color: 'orange',
      teaser: 'Solvable vs perpetual and repair techniques',
      content: {
        intro: 'Gottman estimates 69% of relationship conflicts are PERPETUAL: they never fully resolve because they\'re about personality or value differences. Learning to dialogue about perpetual problems (not solve them) is key to long-term success.',
        techniques: [
          {
            name: 'Identify: Solvable or Perpetual?',
            howTo: [
              'SOLVABLE: Specific situation, has clear solution, both willing to compromise',
              'PERPETUAL: Ongoing disagreement, rooted in personality/values, keeps recurring',
              'Trying to "solve" perpetual problem = frustration and gridlock',
              'Perpetual problems need DIALOGUE, not solution'
            ],
            example: 'Solvable: Who does dishes tonight. Perpetual: One person is neat-freak, other is messy. You manage perpetual, not solve it.',
            why: 'Couples can spend years trying to solve problems that aren\'t solvable, and that breeds resentment'
          },
          {
            name: 'Softened Startup',
            howTo: [
              'Start with appreciation or positive context',
              'State feeling + situation + need (no blame)',
              'Make it about YOUR experience, not their failure',
              'Gottman reports the start predicts the end 96% of the time'
            ],
            example: 'NOT: "You never help!" YES: "I appreciate that you worked late. I felt overwhelmed managing dinner and kids alone. I need us to plan these evenings better."',
            why: 'How you START conflict predicts how it ENDS. Harsh startup = harsh ending.'
          },
          {
            name: 'Take Breaks When Flooded',
            howTo: [
              'Notice physiological flooding (Gottman\'s marker: heart rate above about 100bpm; can\'t think clearly)',
              'Call a timeout: "I need a break. I\'ll be back in 20 minutes."',
              'Self-soothe during break (walk, breathe, NOT ruminate)',
              'Return when calm and continue conversation'
            ],
            example: 'Feeling chest tighten, reactive, saying things you\'ll regret → "I\'m flooded. Let me take 20 minutes, then we\'ll continue."',
            why: 'Flooded conversations cause damage. EVERY TIME. No exceptions. Take the break.'
          },
          {
            name: 'Repair Attempts',
            howTo: [
              'Notice when conversation is going badly',
              'Make a repair bid: "Can we start over?", "I\'m sorry, that came out harsh", "I need a hug"',
              'ACCEPT repair attempts when partner makes them',
              'Repair attempts are relationship lifeline'
            ],
            example: 'Mid-fight: "Wait, this isn\'t going well. I love you and I want to work this out. Can we try again?" Partner: ACCEPTS repair, conversation resets.',
            why: 'Successful couples make AND accept repair attempts. Failed couples ignore them.'
          },
          {
            name: 'Perpetual Problem Dialogue',
            howTo: [
              'Name the perpetual problem explicitly',
              'Each person shares their perspective without trying to convince',
              'Find the DREAMS within the conflict (what each position represents)',
              'Create workable compromise that honours both dreams'
            ],
            example: 'Spend vs Save money: Spender dreams of experiences/freedom. Saver dreams of security/stability. Compromise honours BOTH dreams.',
            why: 'When you understand dreams within conflict, perpetual problems become manageable'
          }
        ],
        remember: 'Conflict isn\'t the problem. It\'s HOW you handle conflict that determines relationship health.',
        warning: 'If contempt has become regular pattern, get professional help. Contempt corrodes relationships fast.',
        practice: 'Next conflict: Identify if solvable or perpetual. If perpetual, shift to dialogue instead of solution-seeking.'
      }
    },

    // ==========================================
    // MODULE 5: DEEPENING INTIMACY
    // ==========================================
    {
      id: 'intimacy-dimensions',
      category: 'Challenge Navigation',
      title: 'The 5 Dimensions of Intimacy',
      icon: Flame,
      color: 'red',
      teaser: 'Beyond sex: building closeness across dimensions',
      content: {
        intro: 'Intimacy isn\'t just sex. Thriving couples cultivate closeness across five dimensions: emotional, physical/sexual, intellectual, experiential, and spiritual. When one dimension suffers, others can compensate.',
        types: [
          {
            name: '💭 Emotional Intimacy',
            purpose: 'Being known and accepted for who you really are',
            why: 'Foundation for all other intimacy: safety enables vulnerability',
            examples: [
              'Sharing fears, dreams, struggles without judgement',
              'Being vulnerable about insecurities',
              'Feeling "seen" and understood by partner',
              'Regular emotional check-ins'
            ],
            magic: 'Emotional intimacy creates safety that makes other intimacy possible',
            avoid: 'Sharing more with friends than partner (intimacy avoidance signal)'
          },
          {
            name: '🔥 Physical/Sexual Intimacy',
            purpose: 'Connection through touch and sexuality',
            why: 'Physical connection bonds and differentiates romantic from platonic relationships',
            examples: [
              'Sexual intimacy that feels connecting (not obligatory)',
              'Non-sexual touch: holding hands, cuddling, massage',
              'Understanding responsive vs spontaneous desire',
              'Scheduling intimacy (not unromantic when life is busy!)'
            ],
            magic: 'Non-sexual touch often INCREASES sexual desire rather than decreasing it',
            avoid: 'Only touching when wanting sex (creates touch aversion)'
          },
          {
            name: '🧠 Intellectual Intimacy',
            purpose: 'Connecting through ideas and perspectives',
            why: 'Shared curiosity and mental stimulation keep relationships interesting',
            examples: [
              'Deep conversations about ideas, not just logistics',
              'Sharing what you\'re learning or thinking about',
              'Respectful debate where both challenge each other',
              'Curiosity about partner\'s perspectives'
            ],
            magic: 'Couples who stay curious about each other\'s minds stay interested in each other',
            avoid: 'Only talking about kids, bills, schedules (loses intellectual connection)'
          },
          {
            name: '🎭 Experiential Intimacy',
            purpose: 'Bonding through shared activities and adventures',
            why: 'Doing new things together creates shared memories and growth',
            examples: [
              'Trying new activities together',
              'Travel and adventure (even small local ones)',
              'Shared hobbies or projects',
              'Novel experiences give relationships a lift (Aron et al., 2000)'
            ],
            magic: 'Novelty rekindles shared excitement',
            avoid: 'Falling into total routine with zero novelty'
          },
          {
            name: '✨ Spiritual Intimacy',
            purpose: 'Connection through shared values and meaning',
            why: 'Shared sense of purpose creates deeper bond',
            examples: [
              'Discussing what matters most to you both',
              'Shared values and how you want to live them',
              'Creating meaning together (family, contribution, legacy)',
              'Religious/spiritual practices if applicable'
            ],
            magic: 'Shared meaning and values are what sustain relationships through decades',
            avoid: 'Never discussing deeper purpose or what you\'re building together'
          }
        ],
        remember: 'All five dimensions don\'t need to be perfect simultaneously. Strengthen different dimensions in different seasons.',
        challenge: 'Rate each intimacy dimension 1-10 this week. Which is strongest? Which needs attention? Discuss with partner.',
        proTip: 'If sexual intimacy is struggling, often it\'s because emotional or experiential intimacy has declined. Address those first.'
      }
    },

    // ==========================================
    // MODULE 6: BUILDING TRUST
    // ==========================================
    {
      id: 'trust-components',
      category: 'Deeper Bonds',
      title: 'The 5 Components of Trust',
      icon: Lock,
      color: 'green',
      teaser: 'Building and maintaining relationship foundation',
      content: {
        intro: 'Trust isn\'t one thing. Relationship Compass breaks it into five everyday components (our own framework, echoing Gottman\'s ATTUNE model of trust-building).',
        blocks: [
          {
            name: '1. Transparency',
            tagline: 'Openness about your life',
            signs: [
              'Sharing your day, thoughts, feelings voluntarily',
              'No secretive pockets of life hidden from partner',
              'Answering questions openly',
              'Appropriate self-disclosure'
            ],
            inAction: 'NOT: Vague about whereabouts. YES: Sharing generally what you\'re doing, who you\'re with.',
            quickFix: 'Volunteer information. Don\'t wait to be asked',
            deeperTruth: 'Transparency ≠ no privacy. You can have private thoughts whilst being open about your life.'
          },
          {
            name: '2. Reliability',
            tagline: 'Doing what you say you\'ll do',
            signs: [
              'Keeping promises (especially small ones)',
              'Following through consistently',
              'Being on time',
              'Not over-promising and under-delivering'
            ],
            inAction: 'Said you\'d put the bins out → Actually put the bins out. Small promises kept = trust built.',
            quickFix: 'Trust is built or eroded in SMALL moments more than big ones',
            deeperTruth: 'Every broken small promise makes a tiny withdrawal from trust bank account'
          },
          {
            name: '3. Attunement',
            tagline: 'Understanding and responding to needs',
            signs: [
              'Knowing what matters to your partner',
              'Noticing their emotional state',
              'Responding to their bids for connection',
              'Remembering what\'s important to them'
            ],
            inAction: 'Partner stressed about work → You notice, ask how they\'re doing, offer support. That\'s attunement.',
            quickFix: 'Pay attention to their inner world, not just surface behaviours',
            deeperTruth: 'Attunement says "I see you, I know you, you matter to me"'
          },
          {
            name: '4. Non-Defensiveness',
            tagline: 'Taking responsibility',
            signs: [
              'Accepting feedback without attacking back',
              'Apologising when you\'re wrong',
              'Not making excuses',
              'Taking ownership of your part'
            ],
            inAction: 'Partner raises concern → You: "You\'re right, I did [thing]. I\'m sorry." NOT: "Yeah but YOU..."',
            quickFix: 'Defensiveness prevents growth and repair; it shuts down communication',
            deeperTruth: 'Taking responsibility paradoxically makes partner less likely to attack'
          },
          {
            name: '5. Selective Sharing',
            tagline: 'Confiding in partner first',
            signs: [
              'Sharing important things with partner before others',
              'Going to partner with problems first',
              'Not making partner last to know',
              'Creating culture of "us first"'
            ],
            inAction: 'Good news or bad news → Tell partner FIRST (not friends, social media, coworkers first).',
            quickFix: 'When partner is last to know repeatedly, they feel like outsider in your life',
            deeperTruth: 'Selective sharing communicates: You\'re my primary relationship'
          }
        ],
        bottomLine: 'Trust is built through consistent small actions across all five components, not occasional grand gestures.',
        practice: 'This week: Rate yourself 1-10 on each component. Which is strongest? Which needs work?',
        proTip: 'If trust is eroding, address it when you notice small signs. Don\'t wait for crisis.'
      }
    },

    {
      id: 'trust-repair',
      category: 'Deeper Bonds',
      title: 'Trust Repair Essentials',
      icon: Shield,
      color: 'teal',
      teaser: 'When trust breaks: the repair process',
      content: {
        intro: 'Repairing a major trust breach (an affair, serious deception, betrayal) usually takes years, not months, and only works if both people fully commit. There are no shortcuts. But repair IS possible with these essentials.',
        steps: [
          {
            number: '1',
            title: 'Full Disclosure (Betraying Partner)',
            description: 'Complete honesty about what happened (no trickle truth)',
            details: [
              'Tell complete truth ONCE (don\'t reveal slowly over time)',
              'Answer all questions honestly',
              'No minimising or justifying',
              'Consider therapist-assisted disclosure for affairs'
            ],
            why: 'Trickle truth retraumatises each time new info emerges. Rip the band-aid once.'
          },
          {
            number: '2',
            title: 'End Betraying Behaviour Immediately',
            description: 'Zero contact with affair partner, complete transparency',
            details: [
              'Cut ALL contact with affair partner (no exceptions)',
              'Block numbers, emails, social media',
              'Open access to phone, accounts, whereabouts',
              'Make ending verifiable'
            ],
            why: 'Ongoing contact makes repair impossible. No negotiation on this.'
          },
          {
            number: '3',
            title: 'Genuine Remorse (Not Just Sorry Caught)',
            description: 'Taking full responsibility without blame-shifting',
            details: [
              'Own actions: "I made this choice. It was wrong."',
              'Express understanding of impact',
              'No blaming partner for your choices',
              'Accept that relationship problems don\'t excuse betrayal'
            ],
            why: 'Without genuine remorse, repair isn\'t possible. It\'s just rug-sweeping'
          },
          {
            number: '4',
            title: 'Patience With Their Process',
            description: 'Accepting that betrayed partner\'s healing takes YEARS',
            details: [
              'Answer same questions repeatedly without irritation',
              'Understand triggers will happen',
              'No "aren\'t you over this yet?"',
              'Accept their timeline, not yours'
            ],
            why: 'You created the trauma. You don\'t get to dictate healing speed.'
          },
          {
            number: '5',
            title: 'Professional Help (Essential)',
            description: 'Couples therapy specialising in betrayal recovery',
            details: [
              'Individual therapy for betrayed partner (trauma processing)',
              'Individual therapy for betraying partner (understanding why)',
              'Couples therapy for repair work',
              'A therapist experienced in affair recovery can guide a process that\'s very hard to manage alone'
            ],
            why: 'Betrayal trauma requires professional support. It\'s not optional'
          },
          {
            number: '6',
            title: 'Consistent Trustworthy Behaviour (Betraying Partner)',
            description: 'Rebuilding through ACTIONS over extended time',
            details: [
              'Maintain complete transparency',
              'Keep all promises',
              'Be reliably where you say you\'ll be',
              'Demonstrate change consistently for YEARS'
            ],
            why: 'Words rebuild nothing. Consistent actions rebuild everything.'
          },
          {
            number: '7',
            title: 'Moving Toward Forgiveness (Betrayed Partner)',
            description: 'When ready (not forced), releasing resentment',
            details: [
              'Forgiveness is for YOU, not them',
              'Forgiveness ≠ forgetting or condoning',
              'It\'s releasing ongoing resentment that harms you',
              'Happens over years, not weeks'
            ],
            why: 'Holding resentment indefinitely prevents YOUR healing, whether you stay or go'
          }
        ],
        remember: 'Not all betrayals can or should be repaired. Both staying and leaving are valid choices. Trust your gut.',
        warning: 'If betraying partner won\'t take full responsibility or end contact with affair partner, repair isn\'t possible.',
        challenge: 'If seeking to repair major breach: Find therapist specialising in betrayal recovery THIS WEEK. Don\'t wait.'
      }
    },

    // ==========================================
    // MODULE 7: GROWING TOGETHER
    // ==========================================
    {
      id: 'interdependence',
      category: 'Deeper Bonds',
      title: 'Healthy Interdependence',
      icon: Users,
      color: 'indigo',
      teaser: 'Supporting growth whilst maintaining alignment',
      content: {
        intro: 'Healthy relationships require differentiation (Bowen): maintaining solid sense of self whilst deeply connected. Not merged (codependency) or separate (emotional distance), but interdependent.',
        hacks: [
          {
            category: 'Differentiation vs Codependency',
            hacks: [
              {
                hack: 'Can be apart without anxiety',
                why: 'Secure attachment allows comfortable autonomy',
                example: 'Partner travelling for work → You miss them but function fine'
              },
              {
                hack: 'Support their individual pursuits',
                why: 'Their growth enriches them, which enriches relationship',
                example: 'Partner wants intensive course → You adjust to support, not resist'
              },
              {
                hack: 'Take responsibility for own emotions',
                why: 'You\'re responsible for YOUR feelings, not their regulation',
                example: 'NOT: "You made me angry" → YES: "I felt angry when..."'
              },
              {
                hack: 'Maintain your own interests/friendships',
                why: 'Individual identity makes you more interesting partner',
                example: 'Have hobbies, friends, goals that are YOURS, not just "ours"'
              }
            ]
          },
          {
            category: 'Supporting Partner\'s Growth',
            hacks: [
              {
                hack: 'Give PERMISSION (emotional, not just logical)',
                why: 'They need to know their growth won\'t threaten relationship',
                example: '"Yes, pursue this. I\'m behind you." (And actually mean it)'
              },
              {
                hack: 'Provide ENCOURAGEMENT',
                why: 'Belief in their capabilities fuels confidence',
                example: '"You can do this. I believe in you." (During setbacks)'
              },
              {
                hack: 'Offer PRACTICAL SUPPORT',
                why: 'Actions > words',
                example: 'Take on responsibilities so they have time for their goal'
              },
              {
                hack: 'Give EMOTIONAL SUPPORT',
                why: 'Be their safe harbour during challenges',
                example: 'Listen to struggles without fixing. Just be present.'
              }
            ]
          },
          {
            category: 'Shared Vision',
            hacks: [
              {
                hack: 'Create explicit shared vision',
                why: 'Prevents drift toward incompatibility',
                example: 'Annual: Discuss where you\'re heading together in 5, 10, 20 years'
              },
              {
                hack: 'Align on major life decisions',
                why: 'Unspoken assumptions about kids, location, career create crises',
                example: 'Before crisis: Discuss children (yes/no/when), location, lifestyle'
              },
              {
                hack: 'Update vision as you evolve',
                why: 'People change, so vision must adapt',
                example: 'What you wanted at 25 may not match what you want at 40'
              },
              {
                hack: 'Use vision to guide decisions',
                why: 'Shared vision provides north star for choices',
                example: 'Job opportunity arises → "Does this move us toward our vision?"'
              }
            ]
          }
        ],
        remember: 'Paradox: The stronger your individual self, the deeper intimacy you can have (because you won\'t lose yourself in merging).',
        practice: 'This week: Identify one individual goal and one shared vision goal. Discuss how to support both.',
        proTip: 'If feeling threatened by partner\'s growth, work on YOUR growth. Insecurity decreases when you\'re also developing.'
      }
    },

    // ==========================================
    // MODULE 8: RELATIONSHIP RESILIENCE
    // ==========================================
    {
      id: 'resilience-practices',
      category: 'Lifetime Thriving',
      title: 'Relationship Resilience',
      icon: TreePine,
      color: 'emerald',
      teaser: 'Maintenance plan for thriving across decades',
      content: {
        intro: 'Thriving relationships aren\'t lucky. They\'re maintained through consistent practices. This isn\'t romantic, but it\'s what actually works across decades.',
        categories: [
          {
            name: 'Daily Practices (30-45 minutes total)',
            items: [
              '✅ One specific appreciation expressed',
              '✅ 5-minute conversation with full presence',
              '✅ Physical affection (kiss, hug, touch)',
              '✅ Brief emotional check-in',
              '✅ Turn towards most of your partner\'s bids',
              '✅ Keep small promises'
            ]
          },
          {
            name: 'Weekly Practices (3-4 hours total)',
            items: [
              '✅ Date night (2-3 hours, protected)',
              '✅ "State of union" check-in (30 min)',
              '✅ Address any small issues before they accumulate',
              '✅ Physical intimacy (if doesn\'t happen spontaneously)',
              '✅ One deeper question you don\'t usually ask'
            ]
          },
          {
            name: 'Monthly Practices (2-3 hours total)',
            items: [
              '✅ Relationship temperature check (rate key dimensions 1-10)',
              '✅ Try one new experience together',
              '✅ Review one relationship module together',
              '✅ Intimacy check-in across all 5 dimensions'
            ]
          },
          {
            name: 'Quarterly Practices (3-5 hours total)',
            items: [
              '✅ Retake relationship assessments, compare to previous',
              '✅ Extended dialogue (deeper conversation)',
              '✅ Growth check-in (individual development + alignment)',
              '✅ Longer date or getaway if possible'
            ]
          },
          {
            name: 'Annual Practices (1-2 days total)',
            items: [
              '✅ Comprehensive relationship review (all 8 modules)',
              '✅ Update shared vision for future',
              '✅ Anniversary celebration',
              '✅ Set relationship goals for year ahead',
              '✅ Celebrate progress and resilience'
            ]
          }
        ],
        remember: 'These practices aren\'t obligations. They\'re preventive maintenance that keeps relationship thriving instead of just surviving.',
        challenge: 'This month: Build your minimal viable maintenance plan. Start with daily + weekly practices. Add monthly/quarterly/annual as you build consistency.',
        proTip: 'Maintenance done with presence = connection. Maintenance done with resentment = just checking boxes. Make it meaningful.'
      }
    },

    {
      id: 'what-predicts-longevity',
      category: 'Lifetime Thriving',
      title: 'What Actually Predicts Longevity',
      icon: TrendingUp,
      color: 'violet',
      teaser: 'Research on what makes relationships last',
      content: {
        intro: 'Decades of research point to what matters for happy long-term relationships. It isn\'t early passion, "meant to be" thinking, or never fighting. It\'s mostly these skills.',
        blocks: [
          {
            name: '✅ What Matters',
            tagline: 'Drawn mainly from Gottman\'s longitudinal research',
            signs: [
              'Keeping positives well ahead of negatives (about 5:1 even during conflict)',
              'Turning towards bids (about 86% in newlyweds who stayed married vs 33% in those who divorced)',
              'Effective conflict management (not absence of conflict)',
              'Friendship foundation (genuinely liking each other)',
              'Shared meaning and vision',
              'Emotional intimacy and vulnerability',
              'Trust components maintained consistently',
              'Both partners committed to ongoing growth'
            ],
            inAction: 'Notice: ALL of these are SKILLS, not luck. All learnable.',
            quickFix: 'Focus on building these skills, not hoping for compatibility',
            deeperTruth: 'Great relationships are BUILT through consistent practice, not FOUND through luck'
          },
          {
            name: '❌ Common Myths',
            tagline: 'What people think matters most',
            signs: [
              'Never fighting (Gottman found stable couples who argue, and stable couples who avoid conflict; how you fight matters more)',
              'Early passion lasting unchanged (the obsessive early phase fades, though romantic love can last; Acevedo & Aron, 2009)',
              'Perfect compatibility (what happens between you predicts quality better than individual traits; Joel et al., 2020)',
              '"Meant to be" thinking (strong destiny beliefs are linked with giving up when problems arise; Knee, 1998)',
              'Grand romantic gestures (nice, but don\'t replace daily small moments)',
              'Similar personalities (some friction is normal and manageable)'
            ],
            inAction: 'Stop comparing your relationship to these myths. They\'re not what works',
            quickFix: 'If you\'re waiting for conflict-free passion forever, you\'ll be disappointed',
            deeperTruth: 'Hollywood relationship narrative is fiction. Real thriving looks different.'
          },
          {
            name: '🔬 Gottman\'s Warning Signs',
            tagline: 'Patterns linked with later divorce in his studies',
            signs: [
              'Presence of Four Horsemen (criticism, contempt, defensiveness, stonewalling)',
              'Harsh startup predicting harsh ending',
              'Failure to accept repair attempts',
              'Negative sentiment override (seeing partner negatively even in neutral moments)',
              'Physiological flooding during conflict',
              'Failed attempts at influence'
            ],
            inAction: 'These patterns were strongly linked with later divorce (Gottman\'s reported 90%+ accuracy figures came from models fitted after the fact, so treat them as warning signs, not a verdict)',
            quickFix: 'If these are regular patterns, get professional help NOW',
            deeperTruth: 'Ignoring these patterns doesn\'t make them go away. It makes them worse'
          },
          {
            name: '💪 Investment Model (Rusbult)',
            tagline: 'Why people stay or go',
            signs: [
              'SATISFACTION: Relationship quality and happiness',
              'INVESTMENT: Time, shared life, what would be lost',
              'ALTERNATIVES: Perceived options outside relationship',
              'COMMITMENT: Psychological attachment to staying'
            ],
            inAction: 'Satisfaction, investment and alternatives shape commitment, and commitment most directly predicts staying (Rusbult, Martz & Agnew, 1998). High satisfaction + investment + low alternatives = high commitment.',
            quickFix: 'Understanding WHY you\'re staying (or considering leaving) requires honest assessment of all four',
            deeperTruth: 'People stay in unsatisfying relationships (high investment, low alternatives) AND leave satisfying ones (low investment, high alternatives)'
          }
        ],
        bottomLine: 'Long-term success isn\'t about finding perfect partner. It\'s about building relationship skills and applying them consistently.',
        practice: 'This week: Assess your relationship against Gottman\'s predictors. Are you building skills that work or relying on myths?'
      }
    },

    {
      id: 'when-seek-help',
      category: 'Lifetime Thriving',
      title: 'When to Seek Professional Help',
      icon: Target,
      color: 'cyan',
      teaser: 'Recognising when you need support',
      content: {
        intro: 'Seeking help isn\'t admission of failure. It\'s intelligent investment in relationship health. Early intervention works. Waiting until crisis makes repair significantly harder.',
        types: [
          {
            name: '🚨 Immediate Help Needed',
            purpose: 'Get professional support NOW',
            why: 'These issues typically require therapist expertise',
            examples: [
              'Affair or emotional infidelity occurred',
              'Considering separation or divorce',
              'One partner has "checked out" emotionally',
              'Abuse present (emotional, physical, financial): see a family violence service rather than couples therapy (see the safety note below)',
              'Same conflict repeating despite best efforts',
              'Communication completely broken down',
              'Contempt has become regular pattern'
            ],
            magic: 'Early intervention prevents problems from becoming intractable',
            avoid: 'Waiting until relationship is in death throes to seek help'
          },
          {
            name: '⚠️ Help Likely Beneficial',
            purpose: 'Consider couples therapy',
            why: 'These patterns indicate need for external support',
            examples: [
              'Intimacy disappeared and won\'t return',
              'Trust breach you can\'t repair alone',
              'Mental health issues affecting relationship',
              'Major life transition creating crisis',
              'Persistent feeling something\'s wrong',
              'Skills from modules aren\'t working'
            ],
            magic: 'Therapist provides objective perspective and structured process',
            avoid: 'Assuming you should handle everything alone without support'
          },
          {
            name: '🧭 Where to Find Help',
            purpose: 'Where to find help',
            why: 'Quality support is available in every country this app serves',
            examples: [
              'Your national relationship-counselling service (Relationships Australia in Australia; the Resources page lists others)',
              'Your country\'s counselling register (e.g. the Australian Counselling Association in Australia)',
              'Psychology Today directory (filter by your country)',
              'Gottman Method therapists (gottman.com)',
              'EFT therapists (iceeft.com)',
              'Your GP or family doctor can refer you'
            ],
            magic: 'A major review found about 70% of couples in therapy show positive change (Lebow et al., 2012)',
            avoid: 'Choosing first available therapist (interview them first)'
          },
          {
            name: '❓ Questions for Therapists',
            purpose: 'Find qualified professional',
            why: 'Not all therapists are trained in couples work',
            examples: [
              '"What\'s your training in couples therapy specifically?"',
              '"What approach do you use?" (Gottman? EFT? Other?)',
              '"Have you worked with [your specific issue]?"',
              '"What\'s your success rate with couples?"',
              '"How long do you expect therapy will take?"'
            ],
            magic: 'Good therapist will answer these confidently and specifically',
            avoid: 'Staying with therapist who isn\'t helping. Try someone else'
          }
        ],
        remember: 'Seeking help when needed is strength, not weakness. Safety first: if you feel afraid of your partner, or they control, threaten or hurt you, couples counselling isn\'t recommended. Contact a family violence service instead (1800RESPECT, 1800 737 732, in Australia; the Resources page lists services for each country this app serves), and call your local emergency number if you\'re in danger.',
        challenge: 'If multiple help signals present, research therapists THIS WEEK. Don\'t wait for crisis.',
        proTip: 'Early intervention (before patterns deeply entrenched) has much better outcomes than crisis intervention.'
      }
    }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-8 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="w-full max-w-5xl max-h-full bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 rounded-2xl shadow-2xl border border-purple-500/20 flex flex-col pointer-events-auto overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                      Relationship Compass
                    </h2>
                    <p className="text-sm text-slate-400">
                      Quick reference guides for the 8 core relationship skills
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    aria-label="Close quick guide"
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                  {!selectedGuide ? (
                    <GuideList
                      guides={quickGuides}
                      onSelectGuide={setSelectedGuide}
                      favouriteGuides={favouriteGuides}
                    />
                  ) : (
                    <GuideContent
                      guide={selectedGuide}
                      onBack={() => setSelectedGuide(null)}
                      onFavourite={() => toggleFavourite(selectedGuide.id)}
                      isFavourite={favouriteGuides.includes(selectedGuide.id)}
                      onCopy={copyToClipboard}
                      copiedItem={copiedItem}
                    />
                  )}
                </div>
              </div>

              {/* Footer Tip */}
              {!selectedGuide && (
                <div className="p-4 border-t border-slate-800 bg-purple-500/10">
                  <p className="text-sm text-purple-300">
                    <Zap className="w-4 h-4 inline mr-1" />
                    <strong>Pro Tip:</strong> These guides distil core principles from the 8 relationship modules.
                    Use them as quick references when navigating relationship challenges.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Guide List Component
const GuideList = ({ guides, onSelectGuide, favouriteGuides }) => {
  const categories = [...new Set(guides.map(g => g.category))];

  return (
    <div className="space-y-6">
      {categories.map(category => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            {category}
          </h3>
          <div className="space-y-2">
            {guides
              .filter(g => g.category === category)
              .map(guide => {
                const Icon = guide.icon;
                return (
                  <motion.button
                    key={guide.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSelectGuide(guide)}
                    className="w-full p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-left group"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 bg-${guide.color}-500/20 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 text-${guide.color}-400`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-medium">{guide.title}</h3>
                          {favouriteGuides.includes(guide.id) && (
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{guide.teaser}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-300 transition-colors mt-2" />
                    </div>
                  </motion.button>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Individual Guide Content Component
const GuideContent = ({ guide, onBack, onFavourite, isFavourite, onCopy, copiedItem }) => {
  const renderContent = () => {
    // Render different layouts based on guide structure
    if (guide.content.blocks) {
      return <BlocksLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.steps) {
      return <StepsLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.types) {
      return <TypesLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.techniques) {
      return <TechniquesLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.hacks) {
      return <HacksLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.categories) {
      return <CategoriesLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to compass
      </button>

      {/* Guide Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">{guide.title}</h3>
            <p className="text-sm text-slate-400">{guide.teaser}</p>
          </div>
          <button
            onClick={onFavourite}
            aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
            aria-pressed={isFavourite}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Star className={`w-5 h-5 ${isFavourite ? 'text-yellow-400 fill-current' : 'text-slate-400'}`} />
          </button>
        </div>
      </div>

      {/* Guide Intro */}
      {guide.content.intro && (
        <Card className="p-4 bg-purple-500/10 border-purple-500/20">
          <p className="text-white">{guide.content.intro}</p>
        </Card>
      )}

      {/* Dynamic Content */}
      {renderContent()}

      {/* Remember / Challenge / Warning */}
      {!guide.content.steps && (
        <>
          {guide.content.remember && typeof guide.content.remember === 'string' && (
            <Card className="p-4 bg-blue-500/10 border-blue-500/20">
              <p className="text-blue-300">
                <Brain className="w-4 h-4 inline mr-1" />
                <strong>Remember:</strong> {guide.content.remember}
              </p>
            </Card>
          )}

          {guide.content.challenge && (
            <Card className="p-4 bg-yellow-500/10 border-yellow-500/20">
              <p className="text-yellow-300">
                <Award className="w-4 h-4 inline mr-1" />
                <strong>Challenge:</strong> {guide.content.challenge}
              </p>
            </Card>
          )}

          {guide.content.proTip && (
            <Card className="p-4 bg-purple-500/10 border-purple-500/20">
              <p className="text-purple-300">
                <Zap className="w-4 h-4 inline mr-1" />
                <strong>Pro Tip:</strong> {guide.content.proTip}
              </p>
            </Card>
          )}

          {guide.content.practice && (
            <Card className="p-4 bg-green-500/10 border-green-500/20">
              <p className="text-green-300">
                <Target className="w-4 h-4 inline mr-1" />
                <strong>Practice:</strong> {guide.content.practice}
              </p>
            </Card>
          )}

          {guide.content.warning && (
            <Card className="p-4 bg-red-500/10 border-red-500/20">
              <p className="text-red-300">
                <MessageSquare className="w-4 h-4 inline mr-1" />
                <strong>Warning:</strong> {guide.content.warning}
              </p>
            </Card>
          )}

          {guide.content.bottomLine && (
            <Card className="p-4 bg-slate-500/10 border-slate-500/20">
              <p className="text-slate-300">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                <strong>In short:</strong> {guide.content.bottomLine}
              </p>
            </Card>
          )}
        </>
      )}
    </motion.div>
  );
};

export default RelationshipCompassQuickGuideHub;
